import React, { useMemo, useEffect, useState } from "react";
import { App as AntApp, Flex, Segmented, Pagination, Spin, Empty } from "antd";
import { AssistanceApi } from "../../../../api";
import { AssistanceItem } from "../AssistanceItem";
import { useAuth } from "../../../../hooks";
import { NEW_ASISTENTES } from "../../../../ui/featureFlags";
import { useTranslation } from "react-i18next";

import styles from "./ListAssistance.module.scss";

const EDUCA360_USER_ID = "67921ab3740e0f2aba258111";

function ListAssistanceInner(props) {
  const { reload, onReload } = props;
  const { accessToken, user } = useAuth();
  const { message } = AntApp.useApp();
  const { t } = useTranslation("assistants");

  const assistanceController = useMemo(() => new AssistanceApi(), []);
  const myUserId = useMemo(() => user?.user?._id || user?._id || null, [user]);

  const [assistances, setAssistances] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("all"); // all | mine

  useEffect(() => {
    let cancelled = false;

    const fetchAssistances = async () => {
      try {
        if (!accessToken) {
          if (!cancelled) setAssistances([]);
          return;
        }

        const response = await assistanceController.getAssistances(
          accessToken,
          page,
        );
        const docs = response?.assistances?.docs || [];

        let filtered = docs;

        if (viewMode === "all") {
          filtered = docs.filter((item) => item.user === EDUCA360_USER_ID);
        } else {
          filtered = myUserId
            ? docs.filter((item) => item.user === myUserId)
            : [];
        }

        if (!cancelled) {
          setAssistances(filtered);
          setPagination({
            total: response?.assistances?.totalDocs || 0,
            page: response?.assistances?.page || 1,
            limit: response?.assistances?.limit || 10,
            pages: response?.assistances?.totalPages || 1,
          });
        }
      } catch (error) {
        message.error(t("list.loadError"));
        if (!cancelled) setAssistances([]);
      }
    };

    fetchAssistances();

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    page,
    reload,
    viewMode,
    myUserId,
    assistanceController,
    message,
    t,
  ]);

  const handleChangeView = (val) => {
    setViewMode(val);
    setPage(1);
  };

  if (assistances === null) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <Flex
        className={styles.filters}
        align="center"
        justify="space-between"
        wrap
        gap={12}
      >
        <Segmented
          className={`${styles.segmented} ${
            NEW_ASISTENTES ? styles.segmentedGreen : ""
          }`}
          value={viewMode}
          onChange={handleChangeView}
          options={[
            { label: t("list.allAssistants"), value: "all" },
            { label: t("list.myAssistants"), value: "mine" },
          ]}
          size="large"
        />
      </Flex>

      {assistances.length === 0 ? (
        <Empty description={t("list.noAssistants")} />
      ) : (
        <div
          className={`${styles.items} ${
            NEW_ASISTENTES ? styles.itemsCompact : ""
          }`}
        >
          {assistances.map((assistance) => (
            <AssistanceItem
              key={assistance._id}
              assistance={assistance}
              onReload={onReload}
              canEdit={viewMode === "mine"}
            />
          ))}
        </div>
      )}

      {pagination && pagination.total > pagination.limit ? (
        <div className={styles.pagination}>
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.limit}
            showSizeChanger={false}
            onChange={(nextPage) => setPage(nextPage)}
          />
        </div>
      ) : null}
    </div>
  );
}

export function ListAssistance(props) {
  return (
    <AntApp>
      <ListAssistanceInner {...props} />
    </AntApp>
  );
}
